import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { postPartnerApplication } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Handshake, Megaphone, TrendingUp, Loader2, CheckCircle2 } from "lucide-react";

const TYPES = [
  { key: "partenaire", Icon: Handshake },
  { key: "sponsor", Icon: Megaphone },
  { key: "investisseur", Icon: TrendingUp },
];

export const JoinUs = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState("partenaire");

  return (
    <section id="join" className="relative py-28 md:py-36" data-testid="join-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-bicenter-violet/15 blur-[180px]"></div>
        <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-full bg-bicenter-cyan/10 blur-[160px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs uppercase tracking-[0.4em] text-bicenter-magenta font-mono">{t.join.kicker}</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
            {t.join.title}
          </h2>
          <p className="mt-6 text-lg text-zinc-400 font-body leading-relaxed">{t.join.subtitle}</p>
        </div>

        <div className="border border-white/10 rounded-3xl bg-[#0c0c0e] p-4 md:p-8 max-w-5xl mx-auto">
          <Tabs value={active} onValueChange={setActive} className="w-full">
            <TabsList className="grid grid-cols-3 bg-white/[0.03] border border-white/5 rounded-full p-1 h-auto">
              {TYPES.map(({ key, Icon }) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  data-testid={`join-tab-${key}`}
                  className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-bicenter-cyan data-[state=active]:to-bicenter-violet data-[state=active]:text-white text-zinc-400 py-3 text-sm font-medium"
                >
                  <Icon size={14} className="mr-2" />
                  <span className="hidden sm:inline">{t.join.tabs[key]}</span>
                  <span className="sm:hidden">{t.join.tabs[key].slice(0, 3)}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {TYPES.map(({ key }) => (
              <TabsContent key={key} value={key} className="mt-8">
                <JoinForm type={key} description={t.join.descriptions[key]} />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
};

const JoinForm = ({ type, description }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "", phone: "",
    website: "", budget_range: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postPartnerApplication({ ...form, application_type: type });
      setDone(true);
      toast.success(t.join.form.success);
      setForm({ name: "", email: "", company: "", role: "", phone: "", website: "", budget_range: "", message: "" });
    } catch (err) {
      toast.error(t.join.form.error);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center text-center py-12">
        <div className="w-16 h-16 rounded-full bg-bicenter-violet/10 border border-bicenter-violet/30 flex items-center justify-center mb-6">
          <CheckCircle2 size={28} className="text-bicenter-violet" />
        </div>
        <h3 className="font-display text-2xl text-white mb-3">BICENTER AI</h3>
        <p className="text-zinc-400 max-w-md">{t.join.form.success}</p>
        <button onClick={() => setDone(false)} className="mt-8 text-sm text-zinc-400 hover:text-white underline">↺</button>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-12 gap-8 items-start">
      <div className="lg:col-span-5">
        <p className="text-zinc-400 leading-relaxed font-body">{description}</p>
        <div className="mt-8 p-5 rounded-2xl bg-gradient-to-br from-bicenter-cyan/10 to-bicenter-violet/10 border border-white/10">
          <p className="text-sm text-white font-display tracking-wide">
            &laquo;&nbsp;Rejoignez les d&eacute;cideurs qui construisent l&apos;&eacute;conomie IA de demain.&nbsp;&raquo;
          </p>
          <p className="mt-2 text-xs text-zinc-500 uppercase tracking-[0.3em]">— BICENTER AI</p>
        </div>
      </div>

      <form onSubmit={submit} className="lg:col-span-7 space-y-4" data-testid={`join-form-${type}`}>
        <div className="grid md:grid-cols-2 gap-4">
          <FormField label={t.join.form.name} required>
            <Input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid={`join-${type}-name`} className="bg-white/[0.03] border-white/10 text-white h-12" />
          </FormField>
          <FormField label={t.join.form.email} required>
            <Input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid={`join-${type}-email`} className="bg-white/[0.03] border-white/10 text-white h-12" />
          </FormField>
          <FormField label={t.join.form.company} required>
            <Input required value={form.company} onChange={(e) => update("company", e.target.value)} data-testid={`join-${type}-company`} className="bg-white/[0.03] border-white/10 text-white h-12" />
          </FormField>
          <FormField label={t.join.form.role}>
            <Input value={form.role} onChange={(e) => update("role", e.target.value)} data-testid={`join-${type}-role`} className="bg-white/[0.03] border-white/10 text-white h-12" />
          </FormField>
          <FormField label={t.join.form.phone}>
            <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid={`join-${type}-phone`} className="bg-white/[0.03] border-white/10 text-white h-12" />
          </FormField>
          <FormField label={t.join.form.website}>
            <Input value={form.website} onChange={(e) => update("website", e.target.value)} data-testid={`join-${type}-website`} className="bg-white/[0.03] border-white/10 text-white h-12" />
          </FormField>
        </div>

        {(type === "sponsor" || type === "investisseur") && (
          <FormField label={t.join.form.budget}>
            <Input value={form.budget_range} onChange={(e) => update("budget_range", e.target.value)} data-testid={`join-${type}-budget`} className="bg-white/[0.03] border-white/10 text-white h-12" />
          </FormField>
        )}

        <FormField label={t.join.form.message} required>
          <Textarea required rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} data-testid={`join-${type}-message`} className="bg-white/[0.03] border-white/10 text-white" />
        </FormField>

        <Button
          type="submit"
          disabled={loading}
          data-testid={`join-${type}-submit`}
          className="w-full h-14 bg-gradient-to-r from-bicenter-violet to-bicenter-magenta text-white text-base font-medium hover:shadow-[0_0_30px_rgba(231,0,255,0.45)]"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> {t.common.loading}
            </span>
          ) : t.join.form.submit}
        </Button>
      </form>
    </div>
  );
};

const FormField = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">
      {label} {required && <span className="text-bicenter-magenta">*</span>}
    </Label>
    {children}
  </div>
);
